import axios from "axios";
import { createContext, useEffect, useRef, useState, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { firebaseConfig } from "../static/firebaseConfig";
import { useLocation } from "react-router-dom";
import { identifyClarityUser, clearClarityUser } from "../utils/clarity";
import * as Sentry from "@sentry/react";

export const FlowContext = createContext();

export function FlowProvider({ children }) {
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [locationPreferences, setLocationPreferences] = useState(null);
  const [pendingJoin, setPendingJoin] = useState(null); // { classId, client }
  const [resource, setResource] = useState(null);
  const timezoneRef = useRef(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const pageUnloadingRef = useRef(false);

  const withRetry = async (fn, retries = 2, delayMs = 500) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        const isNetworkOrTimeout = !err.response || err.code === "ECONNABORTED";
        if (attempt < retries && isNetworkOrTimeout) {
          await new Promise((res) => setTimeout(res, delayMs));
        } else {
          throw err;
        }
      }
    }
  };

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showMapResourceModal, setShowMapResourceModal] = useState(false);
  const [showEventNotificationModal, setShowEventNotificationModal] =
    useState(false);
  const [showAskPhoneModal, setShowAskPhoneModal] = useState(false);
  const [showOfferingModal, setShowOfferingModal] = useState(false);

  const [lastClient, setLastClient] = useState(null);
  const [lastCheckinClient, setLastCheckinClient] = useState(null);
  const [registeredClientPhone, setRegisteredClientPhone] = useState(null);
  const [pendingOffering, setPendingOffering] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(0);

  const [isDebugOn, setIsDebugOn] = useState(false);

  const location = useLocation();

  const app = initializeApp(firebaseConfig);
  const analytics = getAnalytics(app);

  const eventModalEnabled = useMemo(() => {
    console.log("selectedSiteId", selectedSiteId);
    console.log("selectedLocationId", selectedLocationId);
    return (
      new Date() <= new Date("2025-07-25T23:59:59.999Z") &&
      +selectedLocationId != 7 &&
      [30313, 5734086].includes(+selectedSiteId)
    );
  }, [selectedSiteId, selectedLocationId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      pageUnloadingRef.current = true;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    logEvent(analytics, "page_view", { page_path: location.pathname });
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear Clarity identification when navigating to home (reset point)
  useEffect(() => {
    if (location.pathname.match(/\/\d+\/\d+$/) || location.pathname === "/") {
      // Clear identification when on home page or location selection
      console.log("[Clarity] Navigation to home - clearing identification", {
        pathname: location.pathname,
        lastClient: lastClient,
      });
      clearClarityUser("Navigation to home page");
      // Clear Sentry user context
      Sentry.setUser(null);
      setLastClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Function to clear Clarity identification (can be called from Layout idle handler)
  const clearClarityIdentification = () => {
    console.log(
      "[Clarity] Clearing identification via clearClarityIdentification",
      {
        lastClient: lastClient,
        reason: "Idle timeout or manual clear",
      },
    );
    clearClarityUser("Idle timeout or manual clear");
    // Clear Sentry user context
    Sentry.setUser(null);
    setLastClient(null);
  };

  const customAxios = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      locationPreferences?.primaryColor || "#1bc8ca",
    );
    document.documentElement.style.setProperty(
      "--secondary-color",
      locationPreferences?.secondaryColor || "#6c757d",
    );
    if (locationPreferences?.timezone)
      timezoneRef.current = locationPreferences.timezone;
  }, [locationPreferences]);

  const getLocations = async (siteId) => {
    try {
      const response = await withRetry(() =>
        customAxios.get(`/products/checkin/locations`, { params: { siteId } }),
      );
      logEvent(analytics, "get_locations", { siteId });
      return response.data;
    } catch (error) {
      logEvent(analytics, "get_locations_error", {
        siteId,
        error,
        isError: true,
      });
      console.error(error);
      // Capture API errors in Sentry
      Sentry.captureException(error, {
        tags: {
          context: "getLocations",
          siteId: String(siteId),
        },
        extra: {
          response: error?.response?.data,
        },
      });
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const loadSchedules = async (type, date) => {
    try {
      const response = await customAxios.get(`/products/checkin/scheduled`, {
        params: {
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          date,
          type,
        },
      });

      response.data.forEach((schedule) => {
        const start = new Date(schedule.start);
        const end = new Date(schedule.end);

        schedule.startTimeFormatted = start
          .toLocaleString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
          .replace(" AM", "")
          .replace(" PM", "")
          .replace(":00", "");
        schedule.endTimeFormatted = end
          .toLocaleString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
          .replace(":00", "")
          .replace(" AM", "am")
          .replace(" PM", "pm");

        const startDate = new Date(schedule.start);
        const month = startDate.toLocaleString("default", { month: "short" });
        schedule.startDateFormatted = `${month} ${startDate.getDate()}`;
      });

      response.data.sort((a, b) => new Date(a.start) - new Date(b.start));

      // filter if there is no location attribute
      // response.data = response.data.filter(
      //   (schedule) => !!schedule.resourceName
      // );

      logEvent(analytics, "load_schedules", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        date,
        type,
      });

      return response.data;
    } catch (error) {
      logEvent(analytics, "load_schedules_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        date,
        type,
        error,
        isError: true,
      });
      console.error(error);
      // Capture API errors in Sentry
      Sentry.captureException(error, {
        tags: {
          context: "loadSchedules",
          siteId: String(selectedSiteId),
          locationId: String(selectedLocationId),
        },
        extra: {
          date,
          type,
          response: error?.response?.data,
        },
      });
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const loadScheduleById = async (classId) => {
    try {
      setSelectedSchedule(null);
      const response = await customAxios.get(
        `/products/checkin/scheduledById`,
        {
          params: {
            siteId: selectedSiteId,
            classId,
          },
        },
      );

      const schedule = response.data[0];

      const start = new Date(schedule.start);
      const end = new Date(schedule.end);

      schedule.startTimeFormatted = start
        .toLocaleString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
        .replace(" AM", "")
        .replace(" PM", "")
        .replace(":00", "");
      schedule.endTimeFormatted = end
        .toLocaleString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
        .replace(":00", "")
        .replace(" AM", "am")
        .replace(" PM", "pm");

      const startDate = new Date(schedule.start);
      const month = startDate.toLocaleString("default", { month: "short" });
      schedule.startDateFormatted = `${month} ${startDate.getDate()}`;

      setSelectedSchedule(schedule);

      logEvent(analytics, "load_schedule_by_id", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
      });

      return schedule;
    } catch (error) {
      logEvent(analytics, "load_schedule_by_id_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const loadScheduleVisits = async (classId) => {
    try {
      const response = await customAxios.get(`/products/checkin/visitList`, {
        params: {
          siteId: selectedSiteId,
          classId,
        },
      });
      logEvent(analytics, "load_schedule_visits", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
      });
      return response.data;
    } catch (error) {
      // Suppress network errors caused by page reload/navigation (e.g. Clarity idle timeout)
      if (!error.response && pageUnloadingRef.current) return null;
      logEvent(analytics, "load_schedule_visits_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const findClientByPhone = async (phone) => {
    try {
      const response = await withRetry(() =>
        customAxios.get(`/products/checkin/findByPhone`, {
          params: { siteId: selectedSiteId, phone },
        }),
      );
      logEvent(analytics, "find_client_by_phone", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        phone,
      });
      return response.data;
    } catch (error) {
      logEvent(analytics, "find_client_by_phone_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        phone,
        error,
        isError: true,
      });
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          context: "find_client_by_phone",
          siteId: String(selectedSiteId),
          locationId: String(selectedLocationId),
        },
        extra: {
          phone,
          response: error?.response?.data,
        },
      });
      const status = error.response?.status;
      if (status === 400) {
        const err = error.response.data?.error;
        const errMsg =
          typeof err === "string"
            ? err
            : err?.message || err?.toString?.() || "";
        if (!errMsg.trim() || errMsg === "[object Object]") {
          return [];
        }
        throw err instanceof Error ? err : new Error(errMsg);
      }
      if (status >= 500) {
        // Server error (MBO down, etc.) — Sentry already has the real details above
        throw new Error(
          "We couldn't complete the search right now. Please try again.",
        );
      }
      const err = error.response?.data?.error;
      const errMsg =
        typeof err === "string" ? err : err?.message || err?.toString?.();
      throw errMsg
        ? err instanceof Error
          ? err
          : new Error(String(errMsg))
        : new Error("Check your network or try refreshing the page.");
    }
  };

  const joinSchedule = async (classId, client) => {
    try {
      // Clear previous user identification before identifying new user
      if (lastClient && lastClient.id !== client.id) {
        console.log(
          "[Clarity] Different user detected - clearing previous identification",
          {
            previousClientId: lastClient.id,
            newClientId: client.id,
          },
        );
        clearClarityUser("Different user joining schedule");
      }

      const response = await customAxios.post(`/products/checkin/join`, {
        siteId: selectedSiteId,
        classId,
        clientId: client.id,
        crossClientSiteId: client.siteId || null,
        locationId: selectedLocationId,
      });
      logEvent(analytics, "join_schedule", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
        clientId: client.id,
      });
      setLastClient(client);
      if (
        response.data?.offering &&
        locationPreferences?.offeringEnabled !== false
      ) {
        setPendingOffering({ ...response.data.offering, _client: client });
      }

      // Identify user in Clarity
      console.log("[Clarity] User joining schedule - identifying", {
        clientId: client.id,
        clientName: client.name,
        classId,
      });
      identifyClarityUser({
        id: client.id,
        name: client.name,
        phone: client.phone,
        email: client.email,
        siteId: client.siteId,
      });

      // Set user context in Sentry
      Sentry.setUser({
        id: String(client.id),
        username: client.name || "Unknown",
        email: client.email || undefined,
      });

      // Set additional tags
      if (client.siteId) {
        Sentry.setTag("siteId", String(client.siteId));
      }
      if (selectedSiteId) {
        Sentry.setTag("selectedSiteId", String(selectedSiteId));
      }
      if (selectedLocationId) {
        Sentry.setTag("locationId", String(selectedLocationId));
      }

      return response.data;
    } catch (error) {
      logEvent(analytics, "join_schedule_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        classId,
        clientId: client.id,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const quitSchedule = async (classId, clientId, visitId) => {
    try {
      const response = await customAxios.post(`/products/checkin/quit`, {
        siteId: selectedSiteId,
        classId,
        clientId,
        visitId,
        locationId: selectedLocationId,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const checkinSchedule = async (visitId, checkin, clientId) => {
    try {
      // Clear previous user identification if different client
      if (lastClient && lastClient.id !== clientId) {
        console.log(
          "[Clarity] Different user checking in - clearing previous identification",
          {
            previousClientId: lastClient.id,
            newClientId: clientId,
          },
        );
        clearClarityUser("Different user checking in");
      }

      const response = await customAxios.post(`/products/checkin/checkin`, {
        siteId: selectedSiteId,
        visitId,
        checkin,
        locationId: selectedLocationId,
        classId: selectedSchedule?.id,
        clientId,
      });
      logEvent(analytics, "checkin_schedule", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        visitId,
        checkin,
      });

      const newClient = { id: clientId };
      setLastClient(newClient);

      // Identify user in Clarity (with limited info since we only have clientId)
      console.log("[Clarity] User checking in - identifying", {
        clientId: clientId,
        visitId,
        checkin,
      });
      identifyClarityUser({
        id: clientId,
        name: null,
        phone: null,
        email: null,
        siteId: null,
      });

      // Set user context in Sentry (limited info)
      Sentry.setUser({
        id: String(clientId),
      });

      // Set additional tags
      if (selectedSiteId) {
        Sentry.setTag("siteId", String(selectedSiteId));
      }
      if (selectedLocationId) {
        Sentry.setTag("locationId", String(selectedLocationId));
      }

      return response.data;
    } catch (error) {
      logEvent(analytics, "checkin_schedule_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        visitId,
        checkin,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const loadLocationPreferences = async (siteId, locationId) => {
    try {
      const response = await customAxios.get(
        `/products/checkin/customization`,
        {
          params: {
            siteId,
            locationId,
          },
        },
      );
      setLocationPreferences(response.data);
      logEvent(analytics, "load_location_preferences", {
        siteId,
        locationId,
      });
      return response.data;
    } catch (error) {
      logEvent(analytics, "load_location_preferences_error", {
        siteId,
        locationId,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const createAccount = async (data) => {
    try {
      await customAxios.post(`/products/client/create`, data);
      const jwt = await login(data.email, data.password);
      await customAxios.post(
        `/products/client/flush`,
        {},
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        },
      );
      logEvent(analytics, "create_account_kc", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
      });
      return true;
    } catch (error) {
      logEvent(analytics, "create_account_kc_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
        error,
        isError: true,
      });

      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const login = async (email, password) => {
    try {
      const url = `${
        import.meta.env.VITE_AUTH_URL
      }/protocol/openid-connect/token`;
      const data = {
        grant_type: "password",
        client_id: "staff-portal",
        username: email,
        password,
        scope: "openid",
      };
      const response = await axios.post(url, data, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      return response.data.access_token;
    } catch (error) {
      if (error.response) throw error.response.data;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  /* ---------------------------------------------------------------------
     In-kiosk settings. Authenticated by the PIN an admin sets in the staff
     portal — the API verifies it server side, so nothing sensitive ships in
     this bundle.
     --------------------------------------------------------------------- */

  const saveCheckinSettings = async (pin, changes) => {
    try {
      const { data } = await customAxios.post("/products/checkin/settings", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        pin,
        changes,
      });
      logEvent(analytics, "kiosk_settings_saved", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
      });
      return data;
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.error;

      if (status === 401) throw new Error(message || "That PIN didn't work.");
      if (status === 429) {
        throw new Error(
          message || "Too many incorrect PIN attempts. Try again later.",
        );
      }
      if (status === 403) {
        throw new Error(
          message || "No settings PIN is set for this location yet.",
        );
      }
      if (!error?.response) {
        throw new Error("Check your network or try refreshing the page.");
      }
      throw new Error(message || "Couldn't save those settings.");
    }
  };

  const loadCheckinRooms = async (pin) => {
    const { data } = await customAxios.post("/products/checkin/rooms", {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      pin,
    });
    return data;
  };

  const uploadLogo = async (pin, image) => {
    const { data } = await customAxios.post("/products/checkin/logo", {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      pin,
      image,
    });
    return data;
  };

  const resetStoreClient = async (pin) => {
    const { data } = await customAxios.post("/products/checkin/store-client", {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      pin,
    });
    return data;
  };

  const uploadRoomMap = async (pin, resourceId, name, image) => {
    const { data } = await customAxios.post("/products/checkin/room-map", {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      pin,
      resourceId,
      name,
      image,
    });
    return data;
  };

  const createAccountMbo = async (data) => {
    try {
      const cli = await customAxios.post(`/products/client/create-mbo`, {
        ...data,
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        source: "checkin",
      });
      logEvent(analytics, "create_account_mbo", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
      });
      return cli.data;
    } catch (error) {
      logEvent(analytics, "create_account_mbo_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
        error,
        isError: true,
      });

      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const createAccountOld = async (data) => {
    try {
      const cli = await customAxios.post(`/products/client/create-old`, {
        ...data,
        siteId: selectedSiteId,
        source: "checkin",
      });
      logEvent(analytics, "create_account_old", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
      });
      return cli.data;
    } catch (error) {
      logEvent(analytics, "create_account_old_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        email: data.email,
        phone: data.phone,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const voteForEvent = async () => {
    try {
      document.cookie = `votedClientId_${lastClient.id}=true; path=/; max-age=31536000`;
      const response = await customAxios.post(`/products/checkin/vote`, {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        clientId: lastClient.id,
      });
      logEvent(analytics, "vote_for_event", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        clientId: lastClient.id,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const trackOffering = async (
    offeringId,
    action,
    metadata = {},
    clientIdOverride,
  ) => {
    const c = clientIdOverride ?? (lastCheckinClient || lastClient);
    const clientId =
      c != null
        ? (c.id ?? c.Id ?? (typeof c === "object" ? undefined : c))
        : undefined;
    if (!clientId) {
      console.warn("[trackOffering] No clientId available, skipping track");
      return;
    }
    try {
      await customAxios.post(`/products/checkin/offering/track`, {
        offeringId,
        clientId: String(clientId),
        action,
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        metadata,
      });
    } catch (error) {
      console.error("[trackOffering] Failed:", error);
    }
  };

  const recordOfferingInterested = async (
    offeringId,
    client,
    productSnapshot,
  ) => {
    if (!client?.id && !client?.Id) {
      console.warn("[recordOfferingInterested] No clientId available");
      return;
    }
    const clientId = String(client.id ?? client.Id);
    try {
      await customAxios.post(`/products/checkin/offering/interested`, {
        offeringId,
        clientId,
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        clientEmail: client.email ?? null,
        clientPhone: client.phone ?? null,
        clientName: client.name ?? null,
        productSnapshot: productSnapshot
          ? {
              id: productSnapshot.id,
              name: productSnapshot.name,
              type: productSnapshot.type,
              price: productSnapshot.price,
            }
          : null,
      });
    } catch (error) {
      console.error("[recordOfferingInterested] Failed:", error);
    }
  };

  const askedForPhone = async () => {
    try {
      const response = await customAxios.post(
        `/products/checkin/askedForPhone`,
        {
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          clientId: lastClient.id,
        },
      );
      return response.data;
    } catch (error) {
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const saveClientPhone = async (phone) => {
    try {
      const response = await customAxios.post(
        `/products/checkin/updateClientPhone`,
        {
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          clientId: lastClient.id,
          phone: phone.replace(/[^0-9]/g, ""),
        },
      );
      return response.data;
    } catch (error) {
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const openRegisterModal = () => {
    setShowRegisterModal(true);
  };
  const closeRegisterModal = () => {
    setShowRegisterModal(false);
  };
  const openTermsModal = () => {
    setShowRegisterModal(false);
    setShowTermsModal(true);
  };
  const closeTermsModal = () => {
    setShowTermsModal(false);
    setShowRegisterModal(true);
  };
  const doAcceptTerms = () => {
    setAcceptTerms(acceptTerms + 1);
    closeTermsModal();
  };
  const resetTerms = () => {
    setAcceptTerms(0);
  };

  return (
    <FlowContext.Provider
      value={{
        selectedSiteId,
        setSelectedSiteId,
        selectedLocationId,
        setSelectedLocationId,
        selectedSchedule,
        setSelectedSchedule,
        locationPreferences,
        resource,
        setResource,
        showMapResourceModal,
        setShowMapResourceModal,
        timezoneRef,
        getLocations,
        loadSchedules,
        loadScheduleById,
        loadScheduleVisits,
        findClientByPhone,
        joinSchedule,
        checkinSchedule,
        loadLocationPreferences,
        saveCheckinSettings,
        loadCheckinRooms,
        uploadRoomMap,
        uploadLogo,
        resetStoreClient,
        createAccount,
        createAccountMbo,
        showRegisterModal,
        openRegisterModal,
        closeRegisterModal,
        showTermsModal,
        openTermsModal,
        closeTermsModal,
        acceptTerms,
        doAcceptTerms,
        resetTerms,
        pendingJoin,
        setPendingJoin,
        createAccountOld,
        quitSchedule,
        setLocationPreferences,
        isDebugOn,
        setIsDebugOn,
        showEventNotificationModal,
        setShowEventNotificationModal,
        eventModalEnabled,
        voteForEvent,
        showAskPhoneModal,
        setShowAskPhoneModal,
        saveClientPhone,
        askedForPhone,
        clearClarityIdentification,
        showOfferingModal,
        setShowOfferingModal,
        pendingOffering,
        setPendingOffering,
        lastCheckinClient,
        setLastCheckinClient,
        trackOffering,
        recordOfferingInterested,
        registeredClientPhone,
        setRegisteredClientPhone,
      }}
    >
      {children}
    </FlowContext.Provider>
  );
}
